<template>
    <div class="slice slice-trends-hero">
        <div class="slice-trends-hero__polygon"></div>
        <div class="slice-content">
            <TitleBrand :field="slice.primary.title" class="slice-trends-hero__title" data-testid="slice-trends-hero-title" />
            <div class="slice-trends-hero__subtitle" data-testid="slice-trends-hero-subtitle">
                {{ slice.primary.subtitle }}
            </div>
            <div
                v-if="Array.isArray(slice.primary.description) && isFilled.richText(slice.primary.description)"
                :class="[
                    'slice-trends-hero__description',
                    {'slice-trends-hero__description--with-download-report': isFilled.link(slice.primary.downloadCta)},
                ]"
                data-testid="slice-trends-hero-description"
            >
                <PrismicRichText :field="slice.primary.description" />
            </div>
            <div v-if="isFilled.link(slice.primary.downloadCta)" class="slice-trends-hero__download-report">
                <PrismicLink :field="slice.primary.downloadCta">
                    {{ (slice.primary.downloadCta as {text?: string}).text }}
                </PrismicLink>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
    import type {Content} from '@prismicio/client';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText, PrismicLink} from '@prismicio/vue';
    import TitleBrand from '~/components/TitleBrand.vue';

    defineProps(getSliceComponentProps<Content.TrendsHeroSlice>(['slice', 'index', 'slices', 'context']));
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-trends-hero {
        --trends-header-height: 590px;
        --trends-header-nav-height: 72px;
        --trends-polygon-height-variation: 25px;
        --trends-polygon-background-size: auto 250px;
        --trends-background-position: 100% calc(100% + var(--joy-core-spacing-5));
        --trends-title-font-size: 48px;
        --trends-subtitle-font-size: 24px;

        min-height: var(--trends-header-height);
        height: max-content;
        margin-bottom: var(--joy-core-spacing-12);

        &,
        * {
            color: var(--joy-color-neutral-0);
        }

        &__title.title-brand {
            font-size: var(--trends-title-font-size);
            line-height: var(--joy-line-height-small);
        }
        &__subtitle {
            font-size: var(--trends-subtitle-font-size);
            font-family: var(--joy-font-family-title);
            margin-bottom: var(--joy-core-spacing-2);
            line-height: var(--joy-line-height-small);
        }

        &__description {
            max-width: 80%;
            padding-bottom: 50px;

            &--with-download-report {
                padding-bottom: 36px;
            }
        }

        &__polygon {
            position: absolute;
            width: 100%;
            height: 100%;
            background-color: var(--joy-color-primary-50);
            clip-path: polygon(100% 0%, 100% calc(100% - var(--trends-polygon-height-variation)), 0% 100%, 0 0);
            background-position: var(--trends-background-position);
            background-repeat: no-repeat;
            background-image: url('~/assets/svg/trends/head.svg');
            background-size: var(--trends-polygon-background-size);
            padding-bottom: 100px;
        }

        &__download-report {
            color: var(--joy-color-neutral-10);
            width: fit-content;
            background-color: transparent;
            border: 1px solid var(--joy-color-neutral-10);
            font-size: var(--joy-font-size-primary-600);
            padding: var(--joy-core-spacing-3) var(--joy-core-spacing-5);
            cursor: pointer;
            border-radius: var(--joy-core-spacing-10);

            &:hover {
                background-color: var(--joy-color-primary-70);
            }

            @include mq.screen_sm(min) {
                padding: var(--joy-core-spacing-5) var(--joy-core-spacing-9);
            }
        }

        @include mq.screen_xs(min) {
            --trends-header-height: 500px;
        }

        @include mq.screen_sm(min) {
            --trends-header-height: 550px;
            --trends-polygon-height-variation: 50px;
            --trends-polygon-background-size: 350px auto;

            &__title {
                h1 {
                    text-indent: 0.85em hanging;
                }
            }

            &__polygon {
                padding-bottom: 0;
            }

            &__description {
                font-size: var(--joy-font-size-primary-800);
                line-height: var(--joy-line-height-large);
                font-weight: var(--joy-font-weight-normal);
                max-width: 90%;
                padding-bottom: 140px;

                &--with-download-report {
                    padding-bottom: 36px;
                }
            }
        }

        @include mq.screen_lg(min) {
            --trends-header-height: 590px;
            --trends-polygon-height-variation: 80px;
            --trends-polygon-background-size: 630px auto;
            --trends-background-position: calc(100% + 24px) calc(100% + 10px);
            --trends-title-font-size: 85px;
            --trends-subtitle-font-size: 52px;

            .slice-content {
                padding-top: 90px;

                & > * {
                    max-width: calc(80% - var(--joy-core-spacing-10));
                }
            }
        }
    }
</style>
